export type SupportedLanguage = 'ar' | 'en' | 'de';

interface NavTranslations {
  home: string;
  about: string;
  services: string;
  borders: string;
  team: string;
  contact: string;
  credentials: string;
}

interface HeroTranslations {
  title: string;
  tagline: string;
  primaryCta: string;
  secondaryCta: string;
  stats: { value: string; label: string }[];
}

interface AboutTranslations {
  title: string;
  description: string;
  transportLabels: {
    sea: string;
    air: string;
    land: string;
  };
  credentials: {
    title: string;
    intro: string;
    legalNameLabel: string;
    legalName: string;
    fields: { label: string; value: string }[];
    certificateCaption: string;
    openCertificate: string;
  };
}

interface RegionsTranslations {
  title: string;
  subtitle: string;
  description: string;
  countries: { name: string; slug: string; detail: string }[];
}

interface ServiceTranslations {
  title: string;
  cards: { icon: string; title: string; description: string }[];
}

interface QuoteTranslations {
  title: string;
  subtitle: string;
  button: string;
  successMessage: string;
  fields: {
    cargoType: string;
    cargoPlaceholder: string;
    weight: string;
    weightPlaceholder: string;
    border: string;
    borderPlaceholder: string;
    name: string;
    namePlaceholder: string;
    email: string;
    emailPlaceholder: string;
    phone: string;
    phonePlaceholder: string;
  };
  cargoOptions: {
    value: string;
    label: string;
  }[];
  borderOptions: {
    value: string;
    label: string;
  }[];
}

interface BordersTranslations {
  title: string;
  subtitle: string;
  landTitle: string;
  seaTitle: string;
  airTitle: string;
  landList: string[];
  seaList: string[];
  airList: string[];
  mapPoints: { top: string; right: string; label: string }[];
}

interface TestimonialsTranslations {
  title: string;
  subtitle: string;
  cards: { quote: string; author: string; role: string }[];
}

interface FAQTranslations {
  title: string;
  subtitle: string;
  items: { question: string; answer: string }[];
}

interface CertificationsTranslations {
  title: string;
  subtitle: string;
  cards: { icon: string; title: string; description: string }[];
}

interface NewsTranslations {
  title: string;
  cards: { title: string; description: string }[];
  readMore: string;
}

interface TeamTranslations {
  title: string;
  members: { icon: string; name: string; role: string; bio: string }[];
}

interface PartnersTranslations {
  title: string;
  subtitle: string;
}

interface CalculatorTranslations {
  title: string;
  subtitle: string;
  bannerText: string;
  bannerButton: string;
  fields: {
    weight: string;
    weightPlaceholder: string;
    quantity: string;
    quantityPlaceholder: string;
    invoiceValue: string;
    invoiceValuePlaceholder: string;
    hsCode: string;
    hsCodePlaceholder: string;
    hsSearchHint: string;
    noResults: string;
    chapterNote: string;
    border: string;
    borderPlaceholder: string;
  };
  button: string;
  alerts: {
    invalidWeight: string;
    invalidQuantity: string;
    invalidInvoice: string;
    missingHsCode: string;
    hsCodeNotFound: (code: string) => string;
    error: string;
  };
  result: {
    title: string;
    weight: string;
    quantity: string;
    unit: string;
    hsCode: string;
    description: string;
    materialName: string;
    chapterNote: string;
    border: string;
    invoiceValue: string;
    rateImport: string;
    rateService: string;
    importDuty: string;
    serviceFee: string;
    tariffTotal: string;
    borderFee: string;
    advancePercent: string;
    consumptionPercent: string;
    declarationAdvance: string;
    consumptionTax: string;
    grandTotal: string;
    note: string;
    download: string;
  };
}

interface DocumentsTranslations {
  title: string;
  subtitle: string;
  categories: { icon: string; title: string; items: string[] }[];
}

interface ContactTranslations {
  title: string;
  infoTitle: string;
  fields: {
    name: string;
    namePlaceholder: string;
    email: string;
    emailPlaceholder: string;
    phone: string;
    phonePlaceholder: string;
    serviceType: string;
    servicePlaceholder: string;
    message: string;
  };
  serviceOptions: { value: string; label: string }[];
  button: string;
  successMessage: string;
  infoItems: {
    icon: string;
    text: string;
  }[];
}

interface FooterTranslations {
  description: string;
  location: string;
  quickLinksTitle: string;
  contactTitle: string;
  followUs: string;
  rights: string;
}

export interface TranslationContent {
  languageName: string;
  navigation: NavTranslations;
  hero: HeroTranslations;
  about: AboutTranslations;
  regions: RegionsTranslations;
  services: ServiceTranslations;
  quickQuote: QuoteTranslations;
  borders: BordersTranslations;
  testimonials: TestimonialsTranslations;
  faq: FAQTranslations;
  certifications: CertificationsTranslations;
  news: NewsTranslations;
  team: TeamTranslations;
  partners: PartnersTranslations;
  calculator: CalculatorTranslations;
  documents: DocumentsTranslations;
  contact: ContactTranslations;
  footer: FooterTranslations;
}

export const translations: Record<SupportedLanguage, TranslationContent> = {
  ar: {
    languageName: 'العربية',
    navigation: {
      home: 'الرئيسية',
      about: 'عن الشركة',
      services: 'خدماتنا',
      borders: 'المعابر',
      team: 'العاملين',
      contact: 'اتصل بنا',
      credentials: 'السجل التجاري',
    },
    hero: {
      title: 'الشراع للتخليص الجمركي',
      tagline: 'تخليص جمركي في سوريا لشحنات الدول العربية عبر اللاذقية والمعابر',
      primaryCta: 'اكتشف خدماتنا',
      secondaryCta: 'اتصل بنا',
      stats: [
        { value: '30', label: 'سنة خبرة' },
        { value: '5', label: 'معابر' },
        { value: '868', label: 'شحنة سنوياً' },
        { value: '85%', label: 'رضا العملاء' },
      ],
    },
    about: {
      title: 'عن الشركة',
      description:
        'شركة الشراع للتخليص الجمركي مقرها اللاذقية وتعمل عبر الموانئ والمعابر السورية. السجل التجاري 10272، والنشاط المرخّص هو التخليص الجمركي. نخدم المستوردين والمصدّرين داخل سوريا ومن الأردن والعراق والإمارات والسعودية ولبنان عبر البر والبحر والجو.',
      transportLabels: {
        sea: 'الشحن البحري',
        air: 'الشحن الجوي',
        land: 'الشحن البري',
      },
      credentials: {
        title: 'الترخيص والسجل التجاري',
        intro: 'شركة مرخّصة رسمياً ومسجّلة في السجل التجاري — نعرض بياناتنا الرسمية بشفافية لطمأنة عملائنا.',
        legalNameLabel: 'الاسم التجاري المسجّل',
        legalName: 'شركة محمد حاج ياسين وخليل شلفون وشركاهم',
        fields: [
          { label: 'الشكل القانوني', value: 'شركة توصية بسيطة' },
          { label: 'رقم السجل التجاري', value: '10272' },
          { label: 'تاريخ التسجيل', value: '2025/04/13' },
          { label: 'جهة التسجيل', value: 'مديرية التجارة الداخلية وحماية المستهلك — اللاذقية' },
          { label: 'المقر', value: 'اللاذقية — الكاملية، عقار رقم 19/255' },
          { label: 'النشاط المرخّص', value: 'أنشطة التخليص الجمركي' },
        ],
        certificateCaption: 'صورة عن شهادة تسجيل الشركة (الصفحة الأولى)',
        openCertificate: 'فتح صورة الشهادة بحجم أكبر',
      },
    },
    regions: {
      title: 'نخدم التجار في المنطقة',
      subtitle: 'تخليص جمركي في سوريا لعملاء الأردن والعراق والخليج ولبنان ومصر وشمال أفريقيا',
      description:
        'مقرّنا في اللاذقية، وننجز إجراءات التخليص الجمركي داخل سوريا لشحنات الشركات والتجار القادمة من هذه الدول أو المتجهة إليها عبر المعابر والموانئ السورية.',
      countries: [
        { name: 'الأردن', slug: 'jordan', detail: 'تخليص شحنات الاستيراد والتصدير عبر معبر نصيب ومعبر جابر نصيب.' },
        { name: 'العراق', slug: 'iraq', detail: 'تخليص الشحنات عبر معبر أبو كمال ومعبر القامشلي.' },
        { name: 'الإمارات', slug: 'uae', detail: 'تخليص شحنات تجار دبي وأبوظبي عبر ميناء اللاذقية والمطارات السورية.' },
        { name: 'السعودية', slug: 'saudi', detail: 'تخليص بضائع المستوردين والمصدّرين السعوديين عبر المعابر والموانئ السورية.' },
        { name: 'لبنان', slug: 'lebanon', detail: 'تخليص الشحنات من وإلى لبنان عبر الطرق البرية والموانئ السورية.' },
        { name: 'مصر', slug: 'egypt', detail: 'تخليص شحنات التجار المصريين عبر ميناء اللاذقية والمطارات السورية.' },
        { name: 'قطر', slug: 'qatar', detail: 'تخليص شحنات التجار في قطر عبر الموانئ والمطارات السورية.' },
        { name: 'ليبيا', slug: 'libya', detail: 'تخليص شحنات التجار الليبيين عبر ميناء اللاذقية والمنافذ السورية.' },
        { name: 'تونس', slug: 'tunisia', detail: 'تخليص شحنات التجار التونسيين عبر الموانئ والمطارات السورية.' },
        { name: 'الجزائر', slug: 'algeria', detail: 'تخليص شحنات التجار الجزائريين عبر ميناء اللاذقية والمنافذ السورية.' },
        { name: 'المغرب', slug: 'morocco', detail: 'تخليص شحنات التجار المغاربة عبر ميناء اللاذقية والمطارات السورية.' },
      ],
    },
    services: {
      title: 'خدماتنا',
      cards: [
        { icon: '📋', title: 'التخليص الجمركي للبضائع التجارية', description: 'إتمام جميع إجراءات التخليص بدقة وسرعة عالية.' },
        { icon: '📄', title: 'تجهيز الوثائق والشهادات', description: 'تحضير كافة الوثائق الرسمية المطلوبة للاستيراد والتصدير.' },
        { icon: '🚚', title: 'متابعة الشحنات حتى التسليم', description: 'إشراف كامل على الشحنة منذ وصولها حتى التسليم النهائي.' },
        { icon: '🔍', title: 'استشارات التصنيف الجمركي', description: 'مراجعة البنود الجمركية (HS Codes) لضمان دقة الإجراءات.' },
      ],
    },
    quickQuote: {
      title: 'طلب عرض سعر سريع',
      subtitle: 'احصل على عرض سعر خلال 24 ساعة',
      button: 'طلب عرض سعر',
      successMessage: 'شكراً لطلبك! سنقوم بإرسال العرض خلال 24 ساعة.',
      fields: {
        cargoType: 'نوع البضاعة',
        cargoPlaceholder: 'اختر نوع البضاعة',
        weight: 'الوزن (بالأطنان)',
        weightPlaceholder: 'مثال: 10',
        border: 'المعبر',
        borderPlaceholder: 'اختر المعبر',
        name: 'الاسم',
        namePlaceholder: 'اسمك الكريم',
        email: 'البريد الإلكتروني',
        emailPlaceholder: 'example@domain.com',
        phone: 'رقم الهاتف',
        phonePlaceholder: '+963 9XXXXXXXX',
      },
      cargoOptions: [
        { value: 'electronics', label: 'إلكترونيات' },
        { value: 'textiles', label: 'منسوجات' },
        { value: 'food', label: 'مواد غذائية' },
        { value: 'machinery', label: 'آلات ومعدات' },
        { value: 'chemicals', label: 'مواد كيميائية' },
        { value: 'other', label: 'أخرى' },
      ],
      borderOptions: [
        { value: 'land', label: 'معبر بري' },
        { value: 'sea', label: 'ميناء بحري' },
        { value: 'air', label: 'مطار' },
      ],
    },
    borders: {
      title: 'المعابر',
      subtitle: 'نعمل عبر جميع المعابر السورية',
      landTitle: 'المعابر البرية',
      seaTitle: 'المعابر البحرية',
      airTitle: 'المعابر الجوية',
      landList: [
        'معبر نصيب (مع الأردن)',
        'معبر أبو كمال (مع العراق)',
        'معبر القامشلي (مع العراق)',
        'معبر باب الهوى (مع تركيا)',
        'معبر جابر نصيب (مع الأردن)',
      ],
      seaList: ['ميناء اللاذقية', 'ميناء طرطوس', 'ميناء بانياس'],
      airList: ['مطار دمشق الدولي', 'مطار حلب الدولي', 'مطار اللاذقية'],
      mapPoints: [
        { top: '30%', right: '20%', label: 'معبر نصيب' },
        { top: '50%', right: '60%', label: 'معبر أبو كمال' },
        { top: '20%', right: '70%', label: 'معبر باب الهوى' },
        { top: '40%', right: '10%', label: 'ميناء اللاذقية' },
        { top: '30%', right: '15%', label: 'مطار دمشق' },
      ],
    },
    testimonials: {
      title: 'ماذا يقول عملاؤنا',
      subtitle: 'نفتخر بثقة ورضا شركائنا',
      cards: [
        { quote: 'خدمة ممتازة وسرعة في الإنجاز. فريق محترف ومتعاون للغاية.', author: 'أحمد محمد', role: 'مستورد مواد إلكترونية' },
        { quote: 'الشراع سهلت علينا الكثير من الإجراءات المعقدة. احترافية عالية.', author: 'محمود دعبول', role: 'مصدّرة منتجات زراعية' },
        { quote: 'تجربة رائعة! حلول مبتكرة ودعم مستمر في كل خطوة.', author: 'علياء محمود', role: 'صاحبة شركة استيراد وتصدير' },
      ],
    },
    faq: {
      title: 'الأسئلة الشائعة',
      subtitle: 'إجابات عن أهم استفساراتكم',
      items: [
        {
          question: 'من هي شركة الشراع للتخليص الجمركي في سوريا؟',
          answer:
            'مقرها اللاذقية — الكاملية. السجل التجاري 10272 باسم شركة محمد حاج ياسين وخليل شلفون وشركاهم. النشاط المرخّص هو التخليص الجمركي عبر مرفأ اللاذقية والمعابر السورية.',
        },
        {
          question: 'ما أنواع البضائع التي تقومون بتخليصها جمركياً؟',
          answer: 'نقوم بتخليص جميع أنواع البضائع التجارية، بما في ذلك الإلكترونيات، المنسوجات، المواد الغذائية، الآلات، والمواد الكيميائية.',
        },
        {
          question: 'كم تستغرق عملية التخليص الجمركي؟',
          answer: 'تختلف المدة حسب نوع البضاعة والمعبر، لكنها عادةً بين 2 إلى 5 أيام عمل، ونعمل دائماً لتسريع الإجراءات.',
        },
        {
          question: 'هل تقدمون استشارات في التصنيف الجمركي؟',
          answer: 'نعم، لدينا خبراء يساعدونك في اختيار البند الجمركي الصحيح لتجنب الأخطاء أو الغرامات.',
        },
        {
          question: 'ما هي الوثائق المطلوبة للتخليص؟',
          answer: 'الفاتورة التجارية، بوليصة الشحن، شهادة المنشأ، وقائمة التعبئة هي الأهم، وقد نطلب وثائق إضافية حسب نوع البضاعة.',
        },
        {
          question: 'هل يمكنني تتبع شحنتي؟',
          answer: 'نوفر لك رقم تتبع خاص ونبقيك على اطلاع بجميع مراحل التخليص والشحن.',
        },
        {
          question: 'هل تعملون في جميع المعابر السورية؟',
          answer: 'نعم، لدينا شبكة شاملة تغطي المعابر البرية والبحرية والجوية لتقديم خدمة متكاملة.',
        },
        {
          question: 'هل تخدمون عملاء من الأردن والعراق والإمارات والسعودية ولبنان؟',
          answer: 'نعم. ننجز التخليص الجمركي داخل سوريا لشحنات التجار والشركات من الأردن والعراق والإمارات والسعودية ولبنان، عبر معبر نصيب وأبو كمال وميناء اللاذقية وبقية المعابر.',
        },
      ],
    },
    certifications: {
      title: 'شهاداتنا واعتماداتنا',
      subtitle: 'نلتزم بأعلى معايير الجودة والامتثال',
      cards: [
        { icon: '🏅', title: 'شهادة ISO 9001', description: 'نظام إدارة جودة معتمد' },
        { icon: '✅', title: 'اعتماد الجمارك السورية', description: 'مرخصون من الإدارة العامة للجمارك' },
        { icon: '🌍', title: 'عضوية الاتحاد الدولي', description: 'شراكات مع شبكات تخليص عالمية' },
      ],
    },
    news: {
      title: 'الأخبار والتحديثات',
      cards: [
        { title: 'تحديثات قوانين الاستيراد والتصدير', description: 'أحدث التعديلات على اللوائح الجمركية لعام 2024.' },
        { title: 'مشاركتنا في معرض التجارة الدولي', description: 'سنكون ضمن أبرز شركات التخليص في معرض دمشق القادم.' },
        { title: 'توسيع شبكة خدماتنا', description: 'أضفنا معبرين بريين جديدين لتغطية أسرع.' },
      ],
      readMore: 'اقرأ المزيد',
    },
    team: {
      title: 'فريق العمل',
      members: [
        { icon: '👨‍💼', name: 'م. محمد حاج ياسين', role: 'مدير الشركة', bio: 'قيادة استراتيجية لأكثر من 30 عاماً.' },
        { icon: '👨‍💼', name: 'م. فادي طعمة', role: 'مدير العلاقات العامة واللوجستية', bio: 'خبرة في إدارة العلاقات العامة والعمليات اللوجستية.' },
        { icon: '👩‍💼', name: 'رنا برادعي', role: 'المحاسبة', bio: 'خبرة في إدارة الشؤون المالية.' },
        { icon: '👨‍💼', name: 'خليل شلفون', role: 'مخلص جمركي', bio: 'متخصص في إنهاء جميع الإجراءات الجمركية.' },
        { icon: '👨‍💼', name: 'م. الهادي حاج ياسين', role: 'استشاري تخليص جمركي', bio: 'يقدم الدعم الاستشاري والعملي للعملاء.' },
        { icon: '👨‍🚚', name: 'زياد شلا', role: 'مسؤول الشحن البري', bio: 'تنسيق أساطيل النقل البري بكفاءة.' },
        { icon: '👨‍💻', name: 'محمود غريب', role: 'خبير تخليص جمركي', bio: 'متخصص في التصنيف الجمركي.' },
        { icon: '👷‍♂️', name: 'يوسف ديواني', role: 'مسؤول الكشف والتحميل', bio: 'إشراف كامل على عمليات التفريغ والتحميل.' },
      ],
    },
    partners: {
      title: 'شركاؤنا',
      subtitle: 'نتعاون مع أفضل الشركات لتقديم خدمة متكاملة',
    },
    calculator: {
      title: 'الآلة الحاسبة الجمركية',
      subtitle: 'حسب التعريفة الجمركية 2026 — ابحث بالبند أو وصف المادة ثم أدخل قيمة الفاتورة والكمية',
      bannerText: '📄 للاطلاع على التعرفة الجمركية السورية الكاملة:',
      bannerButton: '📥 تحميل التعرفة الجمركية (PDF)',
      fields: {
        weight: 'الوزن القائم (بالأطنان) — لبنود الوزن',
        weightPlaceholder: 'مثال: 5',
        quantity: 'العدد / القطع — لبنود العدد',
        quantityPlaceholder: 'مثال: 1000',
        invoiceValue: 'قيمة الفاتورة (USD) *',
        invoiceValuePlaceholder: 'مثال: 25000',
        hsCode: 'البند الجمركي أو وصف المادة *',
        hsCodePlaceholder: 'ابحث برقم البند أو وصف المادة',
        hsSearchHint: 'اكتب رقماً مثل 0101 أو كلمة مثل خيول أو زيت',
        noResults: 'لا توجد نتائج مطابقة',
        chapterNote: 'ملاحظة الفصل',
        border: 'المعبر',
        borderPlaceholder: 'اختر المعبر',
      },
      button: 'احسب',
      alerts: {
        invalidWeight: 'يرجى إدخال الوزن القائم بشكل صحيح (مطلوب لبنود الوزن).',
        invalidQuantity: 'يرجى إدخال العدد بشكل صحيح (مطلوب لبنود العدد).',
        invalidInvoice: 'يرجى إدخال قيمة الفاتورة بشكل صحيح.',
        missingHsCode: 'يرجى اختيار البند الجمركي من نتائج البحث.',
        hsCodeNotFound: (code) =>
          `البند الجمركي "${code}" غير موجود في التعريفة. تحقق من الرمز أو تواصل معنا.`,
        error: 'حدث خطأ في الحساب. تحقق من المدخلات حسب وحدة البند.',
      },
      result: {
        title: 'تقدير الرسوم',
        weight: 'الوزن القائم:',
        quantity: 'الكمية المحسوبة:',
        unit: 'وحدة الاستيراد:',
        hsCode: 'البند الجمركي:',
        description: 'وصف البند:',
        materialName: 'اسم المادة:',
        chapterNote: 'ملاحظة الفصل:',
        border: 'المعبر:',
        invoiceValue: 'قيمة الفاتورة:',
        rateImport: 'رسم الاستيراد (للوحدة):',
        rateService: 'بدل خدمات الاستيراد (للوحدة):',
        importDuty: 'رسم الاستيراد:',
        serviceFee: 'بدل خدمات الاستيراد:',
        tariffTotal: 'مجموع الرسم والبدل:',
        borderFee: 'بدل/رسم المعبر:',
        advancePercent: 'نسبة سلفة البيان:',
        consumptionPercent: 'نسبة ضريبة الإنفاق:',
        declarationAdvance: 'سلفة البيان:',
        consumptionTax: 'ضريبة الإنفاق الاستهلاكي:',
        grandTotal: 'الإجمالي التقديري:',
        note: '* تقديري حسب التعريفة الجمركية 2026. سلفة البيان وضريبة الإنفاق تُحسبان من قيمة الفاتورة حسب نسب البند. رسم الاستيراد وبدل الخدمات حسب العدد أو الوزن. راجع المخلص للتأكيد النهائي.',
        download: '📥 تحميل التعرفة الجمركية (PDF)',
      },
    },
    documents: {
      title: 'دليل الوثائق المطلوبة',
      subtitle: 'الوثائق الأساسية لعمليات التخليص الجمركي',
      categories: [
        { icon: '📄', title: 'وثائق عامة', items: ['فاتورة تجارية', 'قائمة التعبئة', 'شهادة المنشأ', 'وثيقة الشحن'] },
        { icon: '🏭', title: 'للمواد الغذائية', items: ['شهادة صحية', 'شهادة تحليل', 'رخصة استيراد', 'شهادة HACCP'] },
        { icon: '⚡', title: 'للإلكترونيات', items: ['شهادة المواصفات', 'شهادة السلامة', 'دليل المستخدم', 'شهادة CE'] },
        { icon: '🧪', title: 'للمواد الكيميائية', items: ['شهادة السلامة', 'ورقة بيانات السلامة MSDS', 'رخصة خاصة', 'شهادة التخزين'] },
        { icon: '🚢', title: 'للشحن البحري', items: ['إذن شحن', 'قائمة الحاويات', 'رسوم الميناء', 'إثبات التأمين'] },
        { icon: '✈️', title: 'للشحن الجوي', items: ['أذن التعامل الجمركي', 'بيان الطائرة', 'تقرير الوزن', 'رسوم المطار'] },
      ],
    },
    contact: {
      title: 'اتصل بنا',
      infoTitle: 'معلومات التواصل',
      fields: {
        name: 'الاسم',
        namePlaceholder: 'اسمك الكريم',
        email: 'البريد الإلكتروني',
        emailPlaceholder: 'example@domain.com',
        phone: 'رقم الهاتف',
        phonePlaceholder: '+963 9XXXXXXXX',
        serviceType: 'نوع الخدمة المطلوبة',
        servicePlaceholder: 'اختر نوع الخدمة',
        message: 'الرسالة',
      },
      serviceOptions: [
        { value: 'customs', label: 'التخليص الجمركي' },
        { value: 'documents', label: 'تجهيز الوثائق' },
        { value: 'tracking', label: 'متابعة الشحنات' },
        { value: 'consultation', label: 'استشارات جمركية' },
        { value: 'other', label: 'أخرى' },
      ],
      button: 'إرسال الرسالة',
      successMessage: 'شكراً لتواصلك معنا! سنرد عليك قريباً.',
      infoItems: [
        { icon: '📍', text: 'اللاذقية، سوريا' },
        { icon: '📧', text: 'info@alshiraa-customs.com' },
        { icon: '📱', text: 'موبايل: +963933234488 | +963933612861' },
        { icon: '📞', text: 'أرضي: +963412558610 | +963412558620' },
        { icon: '📠', text: 'فاكس: +963412558630' },
        { icon: '🕒', text: 'السبت - الخميس: 9 ص - 5 م' },
      ],
    },
    footer: {
      description: 'تخليص جمركي في سوريا لعملاء الدول العربية عبر اللاذقية والمعابر.',
      location: 'اللاذقية، سوريا',
      quickLinksTitle: 'روابط سريعة',
      contactTitle: 'معلومات التواصل',
      followUs: 'تابعنا',
      rights: '© جميع الحقوق محفوظة لشركة الشراع.',
    },
  },
  en: {
    languageName: 'English',
    navigation: {
      home: 'Home',
      about: 'About',
      services: 'Services',
      borders: 'Borders',
      team: 'Team',
      contact: 'Contact',
      credentials: 'Commercial Registry',
    },
    hero: {
      title: 'Al Shiraa Customs Clearance',
      tagline: 'Customs clearance in Syria for traders from Jordan, Iraq, UAE, Saudi Arabia and Lebanon',
      primaryCta: 'Explore Services',
      secondaryCta: 'Contact Us',
      stats: [
        { value: '30', label: 'Years of Experience' },
        { value: '5', label: 'Border Points' },
        { value: '868', label: 'Shipments / Year' },
        { value: '85%', label: 'Client Satisfaction' },
      ],
    },
    about: {
      title: 'About Us',
      description:
        'Al Shiraa is a leading customs clearance and logistics company based in Latakia, operating across all Syrian border crossings. We serve importers and exporters from Jordan, Iraq, the UAE, Saudi Arabia and Lebanon, and keep cargo moving by land, sea, and air.',
      transportLabels: {
        sea: 'Sea Freight',
        air: 'Air Freight',
        land: 'Road Freight',
      },
      credentials: {
        title: 'License & Commercial Registry',
        intro: 'We are officially licensed and registered — our commercial details are published here for client trust and transparency.',
        legalNameLabel: 'Registered trade name',
        legalName: 'Mohammed Haj Yassin, Khalil Shalfoun and Partners Company',
        fields: [
          { label: 'Legal form', value: 'Limited partnership (توصية بسيطة)' },
          { label: 'Commercial registry No.', value: '10272' },
          { label: 'Registration date', value: '13 Apr 2025' },
          { label: 'Issuing authority', value: 'Directorate of Internal Trade & Consumer Protection — Latakia' },
          { label: 'Headquarters', value: 'Latakia — Al-Kamiliya, property 19/255' },
          { label: 'Licensed activity', value: 'Customs clearance activities' },
        ],
        certificateCaption: 'Company registration certificate (first page)',
        openCertificate: 'Open full-size certificate image',
      },
    },
    regions: {
      title: 'We serve traders across the region',
      subtitle: 'Syrian customs clearance for clients across the Arab region',
      description:
        'We are based in Latakia and complete customs clearance inside Syria for cargo coming from or going to these countries through Syrian borders and ports.',
      countries: [
        { name: 'Jordan', slug: 'jordan', detail: 'Import and export clearance via Nasib and Jaber Nasib crossings.' },
        { name: 'Iraq', slug: 'iraq', detail: 'Clearance via Abu Kamal and Qamishli crossings.' },
        { name: 'United Arab Emirates', slug: 'uae', detail: 'Clearance for Dubai and Abu Dhabi traders via Latakia port and Syrian airports.' },
        { name: 'Saudi Arabia', slug: 'saudi', detail: 'Clearance for Saudi importers and exporters through Syrian borders and ports.' },
        { name: 'Lebanon', slug: 'lebanon', detail: 'Clearance for shipments to and from Lebanon via land routes and Syrian ports.' },
        { name: 'Egypt', slug: 'egypt', detail: 'Clearance for Egyptian traders via Latakia port and Syrian airports.' },
        { name: 'Qatar', slug: 'qatar', detail: 'Clearance for Qatari traders via Syrian ports and airports.' },
        { name: 'Libya', slug: 'libya', detail: 'Clearance for Libyan traders via Latakia port and Syrian gateways.' },
        { name: 'Tunisia', slug: 'tunisia', detail: 'Clearance for Tunisian traders via Syrian ports and airports.' },
        { name: 'Algeria', slug: 'algeria', detail: 'Clearance for Algerian traders via Latakia port and Syrian gateways.' },
        { name: 'Morocco', slug: 'morocco', detail: 'Clearance for Moroccan traders via Latakia port and Syrian airports.' },
      ],
    },
    services: {
      title: 'Our Services',
      cards: [
        { icon: '📋', title: 'Commercial Customs Clearance', description: 'Complete customs procedures with precision and speed.' },
        { icon: '📄', title: 'Document & Certificate Handling', description: 'Preparing all official documents for import and export.' },
        { icon: '🚚', title: 'Shipment Supervision', description: 'We monitor every shipment from arrival to final delivery.' },
        { icon: '🔍', title: 'HS Code Consulting', description: 'Professional guidance on tariff classification to avoid delays.' },
      ],
    },
    quickQuote: {
      title: 'Quick Quote Request',
      subtitle: 'Receive a detailed quote within 24 hours',
      button: 'Request Quote',
      successMessage: 'Thank you! Your quote will arrive within 24 hours.',
      fields: {
        cargoType: 'Cargo Type',
        cargoPlaceholder: 'Select cargo type',
        weight: 'Weight (tons)',
        weightPlaceholder: 'e.g. 10',
        border: 'Border',
        borderPlaceholder: 'Select border',
        name: 'Name',
        namePlaceholder: 'Full name',
        email: 'Email',
        emailPlaceholder: 'example@domain.com',
        phone: 'Phone Number',
        phonePlaceholder: '+963 9XXXXXXXX',
      },
      cargoOptions: [
        { value: 'electronics', label: 'Electronics' },
        { value: 'textiles', label: 'Textiles' },
        { value: 'food', label: 'Food Products' },
        { value: 'machinery', label: 'Machinery' },
        { value: 'chemicals', label: 'Chemicals' },
        { value: 'other', label: 'Other' },
      ],
      borderOptions: [
        { value: 'land', label: 'Land Crossing' },
        { value: 'sea', label: 'Seaport' },
        { value: 'air', label: 'Airport' },
      ],
    },
    borders: {
      title: 'Border Coverage',
      subtitle: 'Full operations across Syrian land, sea, and air points',
      landTitle: 'Land Crossings',
      seaTitle: 'Seaports',
      airTitle: 'Airports',
      landList: [
        'Nassib Crossing (Jordan)',
        'Abu Kamal (Iraq)',
        'Qamishli (Iraq)',
        'Bab Al-Hawa (Turkey)',
        'Jaber-Nassib (Jordan)',
      ],
      seaList: ['Latakia Port', 'Tartous Port', 'Baniyas Port'],
      airList: ['Damascus International Airport', 'Aleppo International Airport', 'Latakia Airport'],
      mapPoints: [
        { top: '30%', right: '20%', label: 'Nassib Crossing' },
        { top: '50%', right: '60%', label: 'Abu Kamal' },
        { top: '20%', right: '70%', label: 'Bab Al-Hawa' },
        { top: '40%', right: '10%', label: 'Latakia Port' },
        { top: '30%', right: '15%', label: 'Damascus Airport' },
      ],
    },
    testimonials: {
      title: 'What Our Clients Say',
      subtitle: 'Trusted by importers and exporters across Syria',
      cards: [
        { quote: 'Exceptional service and fast turnaround. Highly professional team.', author: 'Ahmed M.', role: 'Electronics Importer' },
        { quote: 'They made complex procedures easy. Accurate, transparent, and punctual.', author: 'Mahmoud Daaboul', role: 'Agricultural Exporter' },
        { quote: 'A reliable partner who always finds a solution for our cargo.', author: 'Alya M.', role: 'Trading Company Owner' },
      ],
    },
    faq: {
      title: 'FAQ',
      subtitle: 'Answers to the most common questions',
      items: [
        {
          question: 'Who is Al-Shiraa customs clearance in Syria?',
          answer:
            'Based in Al-Kamaliya, Latakia. Commercial register 10272, Mohammad Haj Yassin, Khalil Shalfoun & Partners. Licensed for customs clearance through Latakia Port and Syrian land borders.',
        },
        {
          question: 'Which cargo types do you clear?',
          answer: 'All commercial goods including electronics, textiles, food items, machinery, and chemicals.',
        },
        {
          question: 'How long does customs clearance take?',
          answer: 'Depending on cargo and border, procedures take 2–5 business days on average.',
        },
        {
          question: 'Do you assist with HS Code classification?',
          answer: 'Yes, our experts ensure your HS Codes are accurate to avoid penalties or delays.',
        },
        {
          question: 'What documents are required?',
          answer: 'Commercial invoice, bill of lading/airway bill, certificate of origin, and packing list are standard requirements.',
        },
        {
          question: 'Can I track my shipment?',
          answer: 'We provide frequent updates and a dedicated contact to monitor each milestone.',
        },
        {
          question: 'Do you cover all Syrian borders?',
          answer: 'Yes, we operate at every major Syrian land, sea, and air entry point.',
        },
        {
          question: 'Do you serve clients from Jordan, Iraq, the UAE, Saudi Arabia and Lebanon?',
          answer: 'Yes. We complete customs clearance inside Syria for traders and companies from Jordan, Iraq, the UAE, Saudi Arabia and Lebanon, via Nasib, Abu Kamal, Latakia port and other crossings.',
        },
      ],
    },
    certifications: {
      title: 'Certifications & Accreditations',
      subtitle: 'Quality, compliance, and trust',
      cards: [
        { icon: '🏅', title: 'ISO 9001 Certified', description: 'International quality management' },
        { icon: '✅', title: 'Syrian Customs Approval', description: 'Officially licensed clearance agents' },
        { icon: '🌍', title: 'Global Network Member', description: 'Partners across global freight alliances' },
      ],
    },
    news: {
      title: 'News & Updates',
      cards: [
        { title: 'New import/export regulations', description: 'Key highlights from the 2024 customs circular.' },
        { title: 'Al Shiraa at the Damascus Trade Expo', description: 'Meet our team during the region’s largest logistics event.' },
        { title: 'Expansion of land operations', description: 'Two additional crossings now covered for faster routing.' },
      ],
      readMore: 'Read more',
    },
    team: {
      title: 'Our Team',
      members: [
        { icon: '👨‍💼', name: 'Mohamad Haj Yaseen', role: 'General Manager', bio: 'Strategic leadership spanning three decades.' },
        { icon: '👩‍💼', name: 'Rana Baradie', role: 'PR & Accounting Director', bio: 'Financial oversight and stakeholder relations.' },
        { icon: '👨‍💼', name: 'Khalil Shalfoun', role: 'Customs Broker', bio: 'Handles daily customs procedures on-site.' },
        { icon: '👨‍💼', name: 'Alhadi Haj Yaseen', role: 'Customs Consultant', bio: 'Advises clients on compliance and HS codes.' },
        { icon: '👨‍🚚', name: 'Ziad Shalla', role: 'Road Freight Lead', bio: 'Coordinates trucking and inland logistics.' },
        { icon: '👨‍💻', name: 'Mahmoud Gharib', role: 'HS Code Specialist', bio: 'Ensures accurate tariff classification.' },
        { icon: '👷‍♂️', name: 'Yousef Diwani', role: 'Inspection & Loading Supervisor', bio: 'Oversees unloading, tally, and port handling.' },
      ],
    },
    partners: {
      title: 'Partners',
      subtitle: 'We collaborate with top carriers, shipping lines, and agents',
    },
    calculator: {
      title: 'Customs Duty Calculator',
      subtitle: 'Based on the 2026 customs tariff — search by HS code or description, then enter invoice value and quantity',
      bannerText: '📄 Download the full Syrian customs tariff:',
      bannerButton: '📥 Download tariff (PDF)',
      fields: {
        weight: 'Gross weight (tons) — for weight-based items',
        weightPlaceholder: 'e.g. 5',
        quantity: 'Quantity / pieces — for count-based items',
        quantityPlaceholder: 'e.g. 1000',
        invoiceValue: 'Invoice value (USD) *',
        invoiceValuePlaceholder: 'e.g. 25000',
        hsCode: 'HS code or description *',
        hsCodePlaceholder: 'Search by HS code or product description',
        hsSearchHint: 'Type a code like 0101 or a word like horses or oil',
        noResults: 'No matching results',
        chapterNote: 'Chapter note',
        border: 'Border',
        borderPlaceholder: 'Select border',
      },
      button: 'Calculate',
      alerts: {
        invalidWeight: 'Please enter a valid gross weight (required for weight-based items).',
        invalidQuantity: 'Please enter a valid quantity (required for count-based items).',
        invalidInvoice: 'Please enter a valid invoice value.',
        missingHsCode: 'Please select an HS code from the search results.',
        hsCodeNotFound: (code) => `HS Code "${code}" was not found in the tariff.`,
        error: 'Calculation failed. Check inputs against the item unit type.',
      },
      result: {
        title: 'Estimated fees',
        weight: 'Gross weight:',
        quantity: 'Calculated quantity:',
        unit: 'Import unit:',
        hsCode: 'HS Code:',
        description: 'Description:',
        materialName: 'Material:',
        chapterNote: 'Chapter note:',
        border: 'Border:',
        invoiceValue: 'Invoice value:',
        rateImport: 'Import duty (per unit):',
        rateService: 'Import service fee (per unit):',
        importDuty: 'Import duty:',
        serviceFee: 'Import service fee:',
        tariffTotal: 'Duty + service total:',
        borderFee: 'Border fee:',
        advancePercent: 'Declaration advance rate:',
        consumptionPercent: 'Consumption tax rate:',
        declarationAdvance: 'Declaration advance:',
        consumptionTax: 'Consumption expenditure tax:',
        grandTotal: 'Estimated total:',
        note: '* Estimate from the 2026 customs tariff. Advance and consumption tax are % of invoice by HS row. Import duty and service fee are per count or weight. Confirm with your broker.',
        download: '📥 Download customs tariff (PDF)',
      },
    },
    documents: {
      title: 'Required Documents Guide',
      subtitle: 'Essential paperwork for customs clearance',
      categories: [
        { icon: '📄', title: 'General Documents', items: ['Commercial Invoice', 'Packing List', 'Certificate of Origin', 'Bill of Lading'] },
        { icon: '🏭', title: 'Food Imports', items: ['Health Certificate', 'Lab Report', 'Import License', 'HACCP Certificate'] },
        { icon: '⚡', title: 'Electronics', items: ['Conformity Certificate', 'Safety Certificate', 'User Manual', 'CE Declaration'] },
        { icon: '🧪', title: 'Chemicals', items: ['Safety Certificate', 'MSDS Sheet', 'Special Permit', 'Storage Statement'] },
        { icon: '🚢', title: 'Sea Freight', items: ['Port Authorization', 'Container List', 'Port Fees', 'Insurance Proof'] },
        { icon: '✈️', title: 'Air Freight', items: ['Customs Handling Order', 'Aircraft Manifest', 'Weight Report', 'Airport Fees'] },
      ],
    },
    contact: {
      title: 'Contact Us',
      infoTitle: 'Reach Us',
      fields: {
        name: 'Name',
        namePlaceholder: 'Full name',
        email: 'Email',
        emailPlaceholder: 'example@domain.com',
        phone: 'Phone',
        phonePlaceholder: '+963 9XXXXXXXX',
        serviceType: 'Requested Service',
        servicePlaceholder: 'Select a service',
        message: 'Message',
      },
      serviceOptions: [
        { value: 'customs', label: 'Customs Clearance' },
        { value: 'documents', label: 'Document Handling' },
        { value: 'tracking', label: 'Shipment Tracking' },
        { value: 'consultation', label: 'Customs Consulting' },
        { value: 'other', label: 'Other' },
      ],
      button: 'Send Message',
      successMessage: 'Thank you for contacting us! We will respond shortly.',
      infoItems: [
        { icon: '📍', text: 'Latakia, Syria' },
        { icon: '📧', text: 'info@alshiraa-customs.com' },
        { icon: '📱', text: 'Mobile: +963933234488 | +963933612861' },
        { icon: '📞', text: 'Landline: +963412558610 | +963412558620' },
        { icon: '📠', text: 'Fax: +963412558630' },
        { icon: '🕒', text: 'Saturday - Thursday: 9 AM - 5 PM' },
      ],
    },
    footer: {
      description: 'Customs clearance in Syria for traders across the Arab region.',
      location: 'Latakia, Syria',
      quickLinksTitle: 'Quick Links',
      contactTitle: 'Contact Info',
      followUs: 'Follow us',
      rights: '© All rights reserved for Al Shiraa.',
    },
  },
  de: {
    languageName: 'Deutsch',
    navigation: {
      home: 'Startseite',
      about: 'Über uns',
      services: 'Leistungen',
      borders: 'Grenzen',
      team: 'Team',
      contact: 'Kontakt',
      credentials: 'Handelsregister',
    },
    hero: {
      title: 'Al Shiraa Zollabfertigung',
      tagline: 'Zollabfertigung in Syrien für Händler aus Jordanien, Irak, den VAE, Saudi-Arabien und dem Libanon',
      primaryCta: 'Leistungen ansehen',
      secondaryCta: 'Kontakt aufnehmen',
      stats: [
        { value: '30', label: 'Jahre Erfahrung' },
        { value: '5', label: 'Grenzpunkte' },
        { value: '868', label: 'Sendungen / Jahr' },
        { value: '85%', label: 'Kundenzufriedenheit' },
      ],
    },
    about: {
      title: 'Über die Firma',
      description:
        'Al Shiraa ist ein führendes Zollabfertigungs- und Logistikunternehmen mit Sitz in Latakia. Wir betreuen Importeure und Exporteure aus Jordanien, dem Irak, den VAE, Saudi-Arabien und dem Libanon an allen syrischen Grenzübergängen.',
      transportLabels: {
        sea: 'Seefracht',
        air: 'Luftfracht',
        land: 'Straßentransport',
      },
      credentials: {
        title: 'Lizenz & Handelsregister',
        intro: 'Wir sind offiziell lizenziert und eingetragen — unsere Registerdaten veröffentlichen wir transparent für das Vertrauen unserer Kunden.',
        legalNameLabel: 'Eingetragener Firmenname',
        legalName: 'Mohammed Haj Yassin, Khalil Shalfoun und Partner',
        fields: [
          { label: 'Rechtsform', value: 'Kommanditgesellschaft (توصية بسيطة)' },
          { label: 'Handelsregisternr.', value: '10272' },
          { label: 'Eintragungsdatum', value: '13.04.2025' },
          { label: 'Zuständige Behörde', value: 'Direktion für Binnenhandel & Verbraucherschutz — Latakia' },
          { label: 'Sitz', value: 'Latakia — Al-Kamiliya, Grundstück 19/255' },
          { label: 'Lizenzierte Tätigkeit', value: 'Zollabfertigung' },
        ],
        certificateCaption: 'Handelsregisterbescheinigung (erste Seite)',
        openCertificate: 'Zertifikat in voller Größe öffnen',
      },
    },
    regions: {
      title: 'Wir betreuen Händler in der Region',
      subtitle: 'Syrische Zollabfertigung für Kunden in der arabischen Region',
      description:
        'Unser Sitz ist Latakia. Wir erledigen die Zollabfertigung in Syrien für Sendungen aus diesen Ländern oder in diese Länder über syrische Grenzen und Häfen.',
      countries: [
        { name: 'Jordanien', slug: 'jordan', detail: 'Abfertigung über die Übergänge Nasib und Jaber Nasib.' },
        { name: 'Irak', slug: 'iraq', detail: 'Abfertigung über Abu Kamal und Qamishli.' },
        { name: 'Vereinigte Arabische Emirate', slug: 'uae', detail: 'Abfertigung für Händler aus Dubai und Abu Dhabi über den Hafen Latakia und syrische Flughäfen.' },
        { name: 'Saudi-Arabien', slug: 'saudi', detail: 'Abfertigung für saudische Importeure und Exporteure über syrische Grenzen und Häfen.' },
        { name: 'Libanon', slug: 'lebanon', detail: 'Abfertigung von Sendungen von und nach Libanon über Landwege und syrische Häfen.' },
        { name: 'Ägypten', slug: 'egypt', detail: 'Abfertigung für ägyptische Händler über Latakia und syrische Flughäfen.' },
        { name: 'Katar', slug: 'qatar', detail: 'Abfertigung für katarische Händler über syrische Häfen und Flughäfen.' },
        { name: 'Libyen', slug: 'libya', detail: 'Abfertigung für libysche Händler über Latakia und syrische Zugänge.' },
        { name: 'Tunesien', slug: 'tunisia', detail: 'Abfertigung für tunesische Händler über syrische Häfen und Flughäfen.' },
        { name: 'Algerien', slug: 'algeria', detail: 'Abfertigung für algerische Händler über Latakia und syrische Zugänge.' },
        { name: 'Marokko', slug: 'morocco', detail: 'Abfertigung für marokkanische Händler über Latakia und syrische Flughäfen.' },
      ],
    },
    services: {
      title: 'Unsere Leistungen',
      cards: [
        { icon: '📋', title: 'Zollabfertigung für Handelswaren', description: 'Schnelle und korrekte Abwicklung sämtlicher Formalitäten.' },
        { icon: '📄', title: 'Dokumenten- & Zertifikatsservice', description: 'Wir bereiten alle notwendigen Unterlagen für Im- und Export vor.' },
        { icon: '🚚', title: 'Sendungsverfolgung bis zur Zustellung', description: 'Komplette Betreuung der Ladung vom Hafen bis zum Empfänger.' },
        { icon: '🔍', title: 'HS-Code-Beratung', description: 'Fachliche Einstufung Ihrer Waren zur Vermeidung von Verzögerungen.' },
      ],
    },
    quickQuote: {
      title: 'Schnelles Preisangebot',
      subtitle: 'Erhalten Sie Ihr Angebot innerhalb von 24 Stunden',
      button: 'Angebot anfordern',
      successMessage: 'Vielen Dank! Ihr Angebot erhalten Sie innerhalb von 24 Stunden.',
      fields: {
        cargoType: 'Warentyp',
        cargoPlaceholder: 'Warentyp wählen',
        weight: 'Gewicht (Tonnen)',
        weightPlaceholder: 'z. B. 10',
        border: 'Grenzübergang',
        borderPlaceholder: 'Grenzübergang wählen',
        name: 'Name',
        namePlaceholder: 'Ihr vollständiger Name',
        email: 'E-Mail',
        emailPlaceholder: 'beispiel@domain.com',
        phone: 'Telefon',
        phonePlaceholder: '+963 9XXXXXXXX',
      },
      cargoOptions: [
        { value: 'electronics', label: 'Elektronik' },
        { value: 'textiles', label: 'Textilien' },
        { value: 'food', label: 'Lebensmittel' },
        { value: 'machinery', label: 'Maschinen' },
        { value: 'chemicals', label: 'Chemikalien' },
        { value: 'other', label: 'Andere' },
      ],
      borderOptions: [
        { value: 'land', label: 'Landgrenze' },
        { value: 'sea', label: 'See- oder Hafen' },
        { value: 'air', label: 'Flughafen' },
      ],
    },
    borders: {
      title: 'Grenzabdeckung',
      subtitle: 'Umfassende Dienstleistungen an allen syrischen Grenzpunkten',
      landTitle: 'Landgrenzen',
      seaTitle: 'Seeports',
      airTitle: 'Flughäfen',
      landList: [
        'Grenze Nassib (Jordanien)',
        'Abu Kamal (Irak)',
        'Qamishli (Irak)',
        'Bab al-Hawa (Türkei)',
        'Jaber-Nassib (Jordanien)',
      ],
      seaList: ['Hafen Latakia', 'Hafen Tartus', 'Hafen Baniyas'],
      airList: ['Flughafen Damaskus', 'Flughafen Aleppo', 'Flughafen Latakia'],
      mapPoints: [
        { top: '30%', right: '20%', label: 'Grenze Nassib' },
        { top: '50%', right: '60%', label: 'Abu Kamal' },
        { top: '20%', right: '70%', label: 'Bab al-Hawa' },
        { top: '40%', right: '10%', label: 'Hafen Latakia' },
        { top: '30%', right: '15%', label: 'Flughafen Damaskus' },
      ],
    },
    testimonials: {
      title: 'Kundenstimmen',
      subtitle: 'Vertrauen von Importeuren und Exporteuren in ganz Syrien',
      cards: [
        { quote: 'Ausgezeichneter Service und schnelle Abwicklung. Sehr professionelles Team.', author: 'Ahmed M.', role: 'Elektronikimporteur' },
        { quote: 'Komplexe Vorgänge wurden für uns einfach gemacht. Transparent und pünktlich.', author: 'Mahmoud Daaboul', role: 'Landwirtschafts-Exporteur' },
        { quote: 'Ein zuverlässiger Partner, der stets eine Lösung findet.', author: 'Alya M.', role: 'Handelsunternehmerin' },
      ],
    },
    faq: {
      title: 'Häufige Fragen',
      subtitle: 'Alles, was Sie über unsere Leistungen wissen müssen',
      items: [
        {
          question: 'Wer ist Al-Shiraa für die Zollabfertigung in Syrien?',
          answer:
            'Sitz in Al-Kamaliya, Latakia. Handelsregister 10272. Zugelassene Tätigkeit: Zollabfertigung über den Hafen Latakia und die syrischen Landgrenzen.',
        },
        {
          question: 'Welche Warenarten bearbeiten Sie?',
          answer: 'Wir bearbeiten sämtliche Handelswaren – Elektronik, Textilien, Lebensmittel, Maschinen und Chemikalien.',
        },
        {
          question: 'Wie lange dauert die Zollabfertigung?',
          answer: 'Je nach Ware und Grenzübergang beträgt die durchschnittliche Dauer 2–5 Werktage.',
        },
        {
          question: 'Unterstützen Sie bei der HS-Code-Einstufung?',
          answer: 'Ja, unsere Experten stellen korrekte Tarifierung sicher, um Verzögerungen zu vermeiden.',
        },
        {
          question: 'Welche Dokumente werden benötigt?',
          answer: 'Handelsrechnung, Frachtbrief, Ursprungszeugnis und Packliste bilden die Basis. Zusätzliche Nachweise sind je nach Ware möglich.',
        },
        {
          question: 'Kann ich meine Sendung verfolgen?',
          answer: 'Ja, Sie erhalten Updates zu jedem wichtigen Meilenstein der Abfertigung.',
        },
        {
          question: 'Betreuen Sie alle syrischen Grenzen?',
          answer: 'Wir sind an sämtlichen Land-, See- und Luftgrenzen aktiv.',
        },
        {
          question: 'Betreuen Sie Kunden aus Jordanien, Irak, den VAE, Saudi-Arabien und dem Libanon?',
          answer: 'Ja. Wir erledigen die Zollabfertigung in Syrien für Händler und Unternehmen aus Jordanien, Irak, den VAE, Saudi-Arabien und dem Libanon über Nasib, Abu Kamal, den Hafen Latakia und weitere Übergänge.',
        },
      ],
    },
    certifications: {
      title: 'Zertifikate & Akkreditierungen',
      subtitle: 'Qualität, Compliance und Vertrauen',
      cards: [
        { icon: '🏅', title: 'ISO 9001', description: 'International zertifiziertes Qualitätsmanagement' },
        { icon: '✅', title: 'Syrische Zollzulassung', description: 'Offiziell lizenzierte Zollagenten' },
        { icon: '🌍', title: 'Globales Partnernetzwerk', description: 'Mitglieder internationaler Logistiknetzwerke' },
      ],
    },
    news: {
      title: 'News & Updates',
      cards: [
        { title: 'Neue Im- und Exportbestimmungen', description: 'Die wichtigsten Änderungen der Zollvorschriften 2024.' },
        { title: 'Al Shiraa auf der Messe in Damaskus', description: 'Treffen Sie unser Team auf Syriens größter Logistikveranstaltung.' },
        { title: 'Ausbau des Landverkehrs', description: 'Zwei weitere Grenzübergänge für schnellere Routen.' },
      ],
      readMore: 'Mehr erfahren',
    },
    team: {
      title: 'Unser Team',
      members: [
        { icon: '👨‍💼', name: 'Mohamad Haj Yaseen', role: 'Geschäftsführer', bio: 'Strategische Führung und Netzwerkaufbau.' },
        { icon: '👩‍💼', name: 'Rana Baradie', role: 'Leiterin PR & Finanzen', bio: 'Betreut Finanzen sowie Kunden- und Behördenkontakte.' },
        { icon: '👨‍💼', name: 'Khalil Shalfoun', role: 'Zollabfertiger', bio: 'Vor-Ort-Abwicklung sämtlicher Zollformaliäten.' },
        { icon: '👨‍💼', name: 'Alhadi Haj Yaseen', role: 'Zollberater', bio: 'Spezialist für HS-Codes und Importstrategien.' },
        { icon: '👨‍🚚', name: 'Ziad Shalla', role: 'Leiter Straßentransport', bio: 'Koordiniert Lkw-Flotten und Inlandstransport.' },
        { icon: '👨‍💻', name: 'Mahmoud Gharib', role: 'Tarifexperte', bio: 'Sorgt für korrekte Zolltarifnummern.' },
        { icon: '👷‍♂️', name: 'Yousef Diwani', role: 'Leiter Inspektion & Verladung', bio: 'Überwacht Be- und Entladung in Häfen und Lagern.' },
      ],
    },
    partners: {
      title: 'Partner',
      subtitle: 'Gemeinsam mit starken Reedereien und Logistikanbietern',
    },
    calculator: {
      title: 'Zollrechner',
      subtitle: 'Nach Zolltarif 2026 — Suche per HS-Code oder Beschreibung, dann Rechnungswert und Menge eingeben',
      bannerText: '📄 Vollständigen syrischen Zolltarif herunterladen:',
      bannerButton: '📥 Tarif herunterladen (PDF)',
      fields: {
        weight: 'Bruttogewicht (Tonnen) — für gewichtsbasierte Positionen',
        weightPlaceholder: 'z. B. 5',
        quantity: 'Stückzahl — für stückbasierte Positionen',
        quantityPlaceholder: 'z. B. 1000',
        invoiceValue: 'Rechnungswert (USD) *',
        invoiceValuePlaceholder: 'z. B. 25000',
        hsCode: 'HS-Code oder Beschreibung *',
        hsCodePlaceholder: 'Suche per HS-Code oder Warenbeschreibung',
        hsSearchHint: 'Code wie 0101 oder Wort wie Pferde / Öl eingeben',
        noResults: 'Keine Treffer',
        chapterNote: 'Kapitelhinweis',
        border: 'Grenzübergang',
        borderPlaceholder: 'Grenze wählen',
      },
      button: 'Berechnen',
      alerts: {
        invalidWeight: 'Bitte gültiges Bruttogewicht eingeben (für Gewichtspositionen).',
        invalidQuantity: 'Bitte gültige Stückzahl eingeben (für Anzahlpositionen).',
        invalidInvoice: 'Bitte gültigen Rechnungswert eingeben.',
        missingHsCode: 'Bitte einen HS-Code aus den Suchergebnissen wählen.',
        hsCodeNotFound: (code) => `HS-Code "${code}" wurde im Zolltarif nicht gefunden.`,
        error: 'Berechnung fehlgeschlagen. Eingaben anhand der Einheit prüfen.',
      },
      result: {
        title: 'Geschätzte Gebühren',
        weight: 'Bruttogewicht:',
        quantity: 'Berechnete Menge:',
        unit: 'Import-Einheit:',
        hsCode: 'HS-Code:',
        description: 'Beschreibung:',
        materialName: 'Material:',
        chapterNote: 'Kapitelhinweis:',
        border: 'Grenzübergang:',
        invoiceValue: 'Rechnungswert:',
        rateImport: 'Einfuhrzoll (pro Einheit):',
        rateService: 'Import-Servicegebühr (pro Einheit):',
        importDuty: 'Einfuhrzoll:',
        serviceFee: 'Import-Servicegebühr:',
        tariffTotal: 'Zoll + Service gesamt:',
        borderFee: 'Grenzgebühr:',
        advancePercent: 'Vorschuss-Satz:',
        consumptionPercent: 'Verbrauchssteuer-Satz:',
        declarationAdvance: 'Anmeldungsvorschuss:',
        consumptionTax: 'Verbrauchssteuer:',
        grandTotal: 'Geschätzte Gesamtsumme:',
        note: '* Schätzung nach Zolltarif 2026. Vorschuss und Verbrauchssteuer sind % vom Rechnungswert laut HS-Zeile. Zoll und Servicegebühr je Stück oder Gewicht. Bitte mit dem Zollagenten bestätigen.',
        download: '📥 Zolltarif herunterladen (PDF)',
      },
    },
    documents: {
      title: 'Dokumentenleitfaden',
      subtitle: 'Wichtige Unterlagen für die Zollabfertigung',
      categories: [
        { icon: '📄', title: 'Allgemeine Unterlagen', items: ['Handelsrechnung', 'Packliste', 'Ursprungszeugnis', 'Frachtbrief'] },
        { icon: '🏭', title: 'Lebensmittel', items: ['Gesundheitszertifikat', 'Laborbericht', 'Importlizenz', 'HACCP-Nachweis'] },
        { icon: '⚡', title: 'Elektronik', items: ['Konformitätserklärung', 'Sicherheitszertifikat', 'Bedienungsanleitung', 'CE-Erklärung'] },
        { icon: '🧪', title: 'Chemikalien', items: ['Sicherheitszertifikat', 'MSDS', 'Sondergenehmigung', 'Lagererklärung'] },
        { icon: '🚢', title: 'Seefracht', items: ['Hafenfreigabe', 'Containerliste', 'Hafengebühren', 'Versicherungsnachweis'] },
        { icon: '✈️', title: 'Luftfracht', items: ['Zollauftrag', 'Frachtmanifest', 'Wiegeprotokoll', 'Flughafengebühren'] },
      ],
    },
    contact: {
      title: 'Kontakt',
      infoTitle: 'So erreichen Sie uns',
      fields: {
        name: 'Name',
        namePlaceholder: 'Ihr Name',
        email: 'E-Mail',
        emailPlaceholder: 'beispiel@domain.com',
        phone: 'Telefon',
        phonePlaceholder: '+963 9XXXXXXXX',
        serviceType: 'Gewünschte Leistung',
        servicePlaceholder: 'Leistung wählen',
        message: 'Nachricht',
      },
      serviceOptions: [
        { value: 'customs', label: 'Zollabfertigung' },
        { value: 'documents', label: 'Dokumentenservice' },
        { value: 'tracking', label: 'Sendungsverfolgung' },
        { value: 'consultation', label: 'Zollberatung' },
        { value: 'other', label: 'Sonstiges' },
      ],
      button: 'Nachricht senden',
      successMessage: 'Vielen Dank! Wir melden uns in Kürze bei Ihnen.',
      infoItems: [
        { icon: '📍', text: 'Latakia, Syrien' },
        { icon: '📧', text: 'info@alshiraa-customs.com' },
        { icon: '📱', text: 'Mobil: +963933234488 | +963933612861' },
        { icon: '📞', text: 'Festnetz: +963412558610 | +963412558620' },
        { icon: '📠', text: 'Fax: +963412558630' },
        { icon: '🕒', text: 'Samstag – Donnerstag: 9–17 Uhr' },
      ],
    },
    footer: {
      description: 'Zollabfertigung in Syrien für Händler in der arabischen Region.',
      location: 'Latakia, Syrien',
      quickLinksTitle: 'Schnellzugriff',
      contactTitle: 'Kontaktinformationen',
      followUs: 'Folgen Sie uns',
      rights: '© Alle Rechte vorbehalten – Al Shiraa.',
    },
  },
};





